import "./styles/app.css";
import { api } from "./api";
import { logoutPost } from "./auth-logout";
import { startRouter, sharedHistory, type PageRenderer, type Route } from "./router";
import { cachedGet, prefetch } from "./cache";
import { getSimCpf } from "./simulation";
import { buildBreadcrumb } from "./react/nav-registry";
import { codename } from "./react/resource-catalog";
import {
  setIdentity,
  getIdentity,
  getPermissions,
  getDenied,
  homePathForRole,
  resolveAccessDenial,
  type MeResponse,
  type DeniedMap,
} from "./identity";

interface MyPermsResponse {
  role: string;
  is_superadmin: boolean;
  permissions: string[];
  denied: DeniedMap;
}

const appRoot = document.getElementById("app")!;

/** Esconde/remove o splash estático de `index.html` — chamado assim que a
 * 1ª rota terminar de montar (`startRouter`'s `onFirstRender`). */
function hideSplash(): void {
  const splash = document.getElementById("splash-screen");
  if (!splash) return;
  splash.classList.add("is-hidden");
  splash.addEventListener("transitionend", () => splash.remove(), { once: true });
}

// Fetch user identity and permissions before starting the router.
// api.ts redirects to /login/ on 401 so both calls are safe here.
let me_: MeResponse;
let myPermsRes: MyPermsResponse;
try {
  [me_, myPermsRes] = await Promise.all([
    cachedGet<MeResponse>("/api/me/", 15 * 60 * 1000),
    cachedGet<MyPermsResponse>("/api/permissions/me/", 15 * 60 * 1000).catch(
      (): MyPermsResponse => ({ role: "user", is_superadmin: false, permissions: [], denied: {} })
    ),
  ]);
} catch {
  window.location.href = "/login/?next=" + encodeURIComponent(window.location.pathname);
  throw new Error("unauthenticated");
}
setIdentity(me_, new Set(myPermsRes.permissions), myPermsRes.denied);
const me = getIdentity();
const myPermissions = getPermissions();

// Pré-aquece o cache logo após autenticação — cotista vê dados instantâneos
// ao navegar. Superadmin não tem cotas próprias; não pré-carrega — a menos
// que esteja SIMULANDO um CPF (`X-Sim-Cpf`, enviado por api.ts a partir do
// sessionStorage): aí os mesmos endpoints devolvem os dados do simulado e o
// Dashboard os consome igual, então o aquecimento vale do mesmo jeito.
if (!me.is_superadmin || getSimCpf()) {
  // Nível 1: dados agregados (mais rápidos de buscar, mesmos endpoints que o
  // Dashboard já consome sem N+1) — dispara imediatamente.
  prefetch("/api/portal/cotas/", 10 * 60 * 1000);
  prefetch("/api/empreendimentos/meus/", 15 * 60 * 1000);
  prefetch("/api/boletos/consolidado/", 3 * 60 * 1000);
}

/** Nível 2 do pré-aquecimento: por cota (semanas + reservas), até 8 cotas —
 * até 16 chamadas que competem pelo mesmo SQL Server remoto serializado do
 * eSolution Portal. Disparado só depois que a 1ª rota termina de renderizar
 * (via `onFirstRender`, abaixo) — nunca ANTES/durante o carregamento crítico
 * da página em que o usuário está (ex.: Dashboard), senão essas 16 chamadas
 * competem com as do próprio Dashboard e atrasam tudo em 15-20s mesmo com os
 * endpoints agregados já corrigidos. */
function prefetchReservasPorCota(): void {
  if (me.is_superadmin) return;
  cachedGet<{ cotas?: { cota_id: number; integration_id: number }[] }>(
    "/api/portal/cotas/",
    10 * 60 * 1000,
  ).then((resp) => {
    const cotas = resp.cotas ?? [];
    cotas.slice(0, 8).forEach((c) => {
      const base = `/api/portal/cotas/${c.cota_id}`;
      const ig = `integration_id=${c.integration_id}`;
      // Item B/E: `ano` ausente = janela de visualização INTEIRA numa única
      // chamada — mesma URL que a página de Reservas usa (`selectCota`/
      // prefetch por-cota), então o cache bate e o clique na cota é
      // instantâneo. Substitui o antigo pré-aquecimento "só o ano corrente".
      prefetch(`${base}/semanas/?${ig}`, 10 * 60 * 1000);
      prefetch(`${base}/reservas/?${ig}`, 5 * 60 * 1000);
    });
  }).catch(() => { });
}

// Returns true if the current user may access the given permission codename.
// Aceita um array de codenames como OR — basta ter QUALQUER UM deles (usado
// quando mais de uma família de permissão dá acesso à mesma rota).
const hasPermission = (codename: string | string[]): boolean =>
  me.is_superadmin ||
  (Array.isArray(codename) ? codename.some((c) => myPermissions.has(c)) : myPermissions.has(codename));

// Wraps a page renderer with a permission gate. If the user lacks `codename`
// (ou nenhum dos codenames, se array — OR: qualquer um libera, ex. rotas de
// Documentos com 2 famílias de permissão), shows an access-denied toast and
// redirects to the home derived for the user's role (homePathForRole) —
// never a literal path (see comments inside).
function guarded(codename: string | string[], render: PageRenderer): PageRenderer {
  return async (root: HTMLElement): Promise<void | boolean> => {
    if (!hasPermission(codename)) {
      // `resolveAccessDenial` (identity.ts) decide o destino a partir do papel
      // efetivo (`homePathForRole` — mesma regra de `resolveHardRedirect`,
      // abaixo, nunca duplicada) e sinaliza `redirectTo: null` no caso
      // degenerado (a rota negada já é a própria home do papel — não sobra
      // pra onde redirecionar sem repetir a negação em loop). Corrige a
      // cascata do bug ClickUp #868kt003c/"módulo não liberado": um
      // `role=admin` negado numa rota `/painel/*` caía antes no literal
      // "/app/dashboard" — zona do cotista, também negada para ele — e a 2ª
      // passagem confundia isso com "conta sem acesso ao portal" e deslogava
      // via `logoutPost`. `denied` só é populado para `role=admin` (Admin
      // Gestor) — usa a copy comercial do superadmin quando o codename está
      // lá (teaser); para `role=user` (Admin Operador), sempre vazio, cai no
      // toast genérico, independente do estado visual da sidebar.
      //
      // Codename em array (OR): todos os codenames levam à mesma rota, então
      // basta a 1ª entrada de `denied` encontrada entre eles pra ter teaser —
      // não há como haver conflito de copy porque é a mesma página.
      const deniedEntry = Array.isArray(codename)
        ? codename.map((c) => getDenied()[c]).find((entry) => entry !== undefined)
        : getDenied()[codename];
      const { toastMessage, redirectTo } = resolveAccessDenial(
        me,
        window.location.pathname,
        deniedEntry,
      );
      // Sempre exibido, mesmo no caso degenerado — nenhum acesso negado pode
      // terminar sem feedback. import() dinâmico (não estático no topo do
      // arquivo): `main.ts` é o entry point e `vite.config.ts::manualChunks`
      // põe todo `node_modules` no chunk `vendor` — um import estático de
      // `snackbar` tornaria React/MUI/sonner alcançáveis a partir do entry e
      // entraria no eager (mesma razão de `guardedSuperadmin` abaixo).
      void import("./react/vendor/snackbar").then(({ toast }) => toast.error(toastMessage));

      if (redirectTo === null) {
        // Caso degenerado: sessão válida, só não há NENHUMA página própria do
        // papel liberada. Diferente de "conta sem acesso ao portal" (estado
        // não decidido aqui — `logoutPost` continua reservado ao logout
        // voluntário via account-popover.ts). Mostra o erro (acima) e, no
        // lugar do `render(root)` que normalmente aconteceria, substitui a
        // área de conteúdo por um placeholder (nunca deixa resíduo da
        // navegação anterior ou área vazia) — sem redirecionar/deslogar.
        await renderAccessDeniedReact(root);
        return;
      }
      history.replaceState({}, "", redirectTo);
      window.dispatchEvent(new PopStateEvent("popstate"));
      // Redirect DINÂMICO (decidido em runtime, dependendo de hasPermission):
      // avisa handle() (router.ts) para não finalizar o ciclo de 1ª
      // renderização nesta passagem — quem finaliza é a chamada reentrante
      // (síncrona) disparada pelo popstate acima, quando a rota real (home)
      // montar de verdade.
      return true;
    }
    return await render(root);
  };
}

// Wraps a page renderer with a superadmin-only gate — mesmo fluxo de negação
// de `guarded` acima (toast + redirect para /app/dashboard, incluindo o caso
// especial de fallback quando a própria rota negada É o dashboard), mas sem
// depender de nenhum codename do catálogo: decisão de produto (2026-08-24) de
// que certas telas (Documentação da API, Sitemap) são exclusivas de
// superadmin, não liberáveis a admin via Perfis de Acesso.
function guardedSuperadmin(render: PageRenderer): PageRenderer {
  return async (root: HTMLElement): Promise<void | boolean> => {
    if (!me.is_superadmin) {
      if (window.location.pathname.startsWith("/app/dashboard")) {
        logoutPost("/login/?erro=nao-autorizado");
        return;
      }
      // import() dinâmico — mesma razão de `guarded()` acima (bundle eager).
      void import("./react/vendor/snackbar").then(({ toast }) =>
        toast.error("Você não tem permissão para acessar esta página."),
      );
      history.replaceState({}, "", "/app/dashboard");
      window.dispatchEvent(new PopStateEvent("popstate"));
      return true;
    }
    return await render(root);
  };
}

/**
 * Constrói uma `Route` de redirect interno da SPA — `render()` recalcula o
 * destino a partir do path/search atuais (via `resolveTarget`) e dispara
 * `history.replaceState` + `popstate` sintético, que reentra SINCRONAMENTE em
 * `handle()` (router.ts) para montar a rota real. `redirect: true` avisa
 * `handle()` para pular `mountChrome()` nesta passagem e não finalizar o
 * ciclo de 1ª renderização aqui — quem finaliza é a chamada aninhada.
 */
function defineInternalRedirect(
  path: Route["path"],
  key: string,
  resolveTarget: (path: string, search: string) => string,
): Route {
  return {
    path,
    key,
    breadcrumb: [],
    // Formalidade de tipo: `redirect: true` faz `handle()` pular `mountChrome()`
    // por completo, então este valor nunca é lido. Declarado só porque `chrome`
    // passou a ser obrigatório em `Route` (Estágio 4 do Sunset, ADR-009).
    chrome: "react" as const,
    redirect: true,
    render: () => {
      const target = resolveTarget(window.location.pathname, window.location.search);
      history.replaceState({}, "", target);
      window.dispatchEvent(new PopStateEvent("popstate"));
    },
  };
}

/**
 * Sunset ADR-009, Estágio 4 — as 8 rotas placeholder "Em breve" (antes
 * `render: renderComingSoon(label)`, Vanilla) compartilham a mesma forma:
 * só variam path/key/label/breadcrumb. Fábrica única (mesmo padrão de
 * `defineInternalRedirect` acima) evita repetir a closure de shell +
 * `import()` 8 vezes no bundle eager — `main.ts` é o entry point, então o
 * corpo de cada `render()` inline soma ao chunk eager mesmo com o `import()`
 * sendo dinâmico.
 *
 * 04-09-2026 (encerramento da ADR-029): `zone`/`permission` passam a ser
 * opcionais para dar suporte às 5 rotas `/app/*` do cotista (antes dados
 * mockados) que também viraram "Em breve" — sem eles, o comportamento das 8
 * rotas admin originais (zone "superadmin", sem gate de permissão) não muda.
 */
function defineComingSoonRoute(
  path: Route["path"],
  key: string,
  label: string,
  breadcrumb: Route["breadcrumb"],
  options?: { zone?: "user" | "superadmin"; permission?: string | string[] },
): Route {
  const zone = options?.zone ?? "superadmin";
  const renderPage = (root: HTMLElement) => {
    const shell = {
      label,
      activeRoute: key,
      breadcrumb,
      zone,
      permissions: myPermissions,
      isSuperadmin: me.is_superadmin,
      userRole: me.role,
    };
    return import("./react/pages/coming-soon").then((m) => m.renderComingSoonPage(root, shell));
  };
  return {
    path,
    key,
    breadcrumb,
    chrome: "react" as const,
    render: options?.permission ? guarded(options.permission, renderPage) : renderPage,
  };
}

const routes = [
  // ── Superadmin area ────────────────────────────────────────────────────
  // Redirect da raiz: "/" era a landing histórica da área superadmin (porte
  // 1:1 de pages/dashboard.ts, pré-convenção de URL "/painel/"), duplicando a
  // home real do admin (/painel/admin/, destino de post_login_redirect e do
  // próprio sidebar). Nenhum link do app aponta para "/" — só alcançável
  // digitando a URL direto. `resolveHardRedirect` já rebaixa "/painel/*" para
  // /app/dashboard se o usuário não for admin/superadmin, então este redirect
  // incondicional cobre os 2 papéis.
  defineInternalRedirect("/", "admin-dashboard", (_path, search) => "/painel/admin/" + search),
  {
    path: "/painel/admin/api-docs/",
    key: "api-docs",
    // Plano "Mídia e uploads por módulo" (Fase 5): breadcrumb literal →
    // buildBreadcrumb (registry ganhou a entrada "api-docs").
    breadcrumb: buildBreadcrumb("api-docs", { isSuperadmin: me.is_superadmin }),
    // Sunset ADR-009, Estágio 2, Lote 1 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/api-docs.ts) removido; rota resolve direto para a página React.
    chrome: "react" as const,
    // Guardada por superadmin (decisão de produto 2026-08-24: exclusiva de
    // superadmin, não liberável a admin via Perfis de Acesso). Antes a rota
    // não tinha guard algum: o item de menu era o único freio, e qualquer
    // usuário logado que digitasse a URL abria a página.
    render: guardedSuperadmin((root: HTMLElement) => {
      const shell = {
        activeRoute: "api-docs",
        breadcrumb: buildBreadcrumb("api-docs", { isSuperadmin: me.is_superadmin }),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/api-docs").then((m) => m.renderApiDocsPage(root, shell));
    }),
  },
  {
    path: "/painel/admin/permissoes/catalogo/",
    key: "perm-catalogo",
    breadcrumb: buildBreadcrumb("permission-catalog", { isSuperadmin: me.is_superadmin }),
    // Onda 3, Bloco C, página 11 (ADR-008 — ver docs/migracao-react/ONDA-3-plano-tecnico.md).
    // Rota double-mounted com "admin-catalogo" abaixo — esta não tinha guarded()
    // no vanilla, preservado exatamente assim (achado registrado em
    // BACKLOG-HARDENING-PERMISSOES.md, mitigado no backend por IsSuperadmin).
    // Sunset ADR-009, Estágio 2, Lote 6-B — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Bloco condicional
    // removido (dead code desde o Estágio 1).
    // ADR-014, Fase 0: breadcrumb vem do registry (react/nav-registry.ts), não
    // mais array literal.
    chrome: "react" as const,
    render: (root: HTMLElement) => {
      const shell = {
        activeRoute: "perm-catalogo",
        breadcrumb: buildBreadcrumb("permission-catalog", { isSuperadmin: me.is_superadmin }),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/catalogo").then((m) => m.renderCatalogoPage(root, shell));
    },
  },
  {
    path: "/painel/admin/permissoes/catalogo/editar/",
    key: "perm-catalogo-editar",
    breadcrumb: buildBreadcrumb("permission-catalog", { isSuperadmin: me.is_superadmin }, [{ label: "Permissão" }]),
    // "Nova Permissão" e "Editar Permissão" viraram a MESMA página própria
    // (não mais modos de catalogo-form-dialog.tsx) — identifica o modo por
    // query params (`?action=newpermission` cria, `?categoria=&subcategoria=&nome=`
    // edita, chave composta sem id numérico). O crumb estático acima fica
    // genérico porque é resolvido antes de saber a querystring; o real
    // (exibido de fato) sai dinâmico abaixo, resolvido a cada navegação.
    // Double-mounted com "admin-catalogo-editar" abaixo, mesma assimetria de
    // guarded() da rota de lista correspondente (esta não tem, a de
    // /admin/catalogo/ tem).
    chrome: "react" as const,
    render: (root: HTMLElement) => {
      const isCreate = new URLSearchParams(window.location.search).get("action") === "newpermission";
      const shell = {
        activeRoute: "perm-catalogo-editar",
        breadcrumb: buildBreadcrumb("permission-catalog", { isSuperadmin: me.is_superadmin }, [
          { label: isCreate ? "Nova Permissão" : "Editar Permissão" },
        ]),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
        listUrl: "/painel/admin/permissoes/catalogo/",
      };
      return import("./react/pages/catalogo-editar").then((m) => m.renderCatalogoEditarPage(root, shell));
    },
  },
  {
    path: "/painel/admin/permissoes/perfis/",
    key: "perm-grupos",
    breadcrumb: buildBreadcrumb("permission-profiles", { isSuperadmin: me.is_superadmin }),
    // Sunset ADR-009, Estágio 2, Lote 2 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/permissions/grupos.ts + admin/grupos.ts, wrapper) removido.
    // ADR-014, Fase 0: breadcrumb vem do registry.
    chrome: "react" as const,
    render: (root: HTMLElement) => {
      const shell = {
        activeRoute: "perm-grupos",
        breadcrumb: buildBreadcrumb("permission-profiles", { isSuperadmin: me.is_superadmin }),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
        isOperador: me.is_operador,
      };
      return import("./react/pages/grupos").then((m) => m.renderGruposPage(root, shell));
    },
  },
  {
    path: /^\/painel\/admin\/permissoes\/usuarios\/(admin|user)?\/?$/,
    key: "perm-usuarios",
    breadcrumb: buildBreadcrumb("users", { isSuperadmin: me.is_superadmin }),
    // Sunset ADR-009, Estágio 2, Lote 6 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Bloco condicional
    // removido (dead code desde o Estágio 1). `pages/permissions/usuarios.ts`
    // **não foi apagado** — gate de dependências cruzadas encontrou que
    // `pages/admin/usuarios.ts` (Vanilla ainda ativa, Estágio 0 pendente)
    // depende dele via `renderUsuarios as renderUsuariosSuperadmin`. Arquivo
    // fica retido até o Estágio 0 decidir o destino de `admin/usuarios.ts`.
    // ADR-014, Fase 0: breadcrumb vem do registry.
    chrome: "react" as const,
    render: (root: HTMLElement) => {
      const shell = {
        activeRoute: "perm-usuarios",
        breadcrumb: buildBreadcrumb("users", { isSuperadmin: me.is_superadmin }),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
        formBase: "/painel/admin/permissoes/usuarios/",
        isGestor: me.is_gestor,
        isOperador: me.is_operador,
      };
      return import("./react/pages/usuarios").then((m) => m.renderUsuariosPage(root, shell));
    },
  },
  {
    path: /^\/painel\/admin\/permissoes\/usuarios\/(novo|\d+\/editar)\/?$/,
    key: "perm-usuarios",
    breadcrumb: buildBreadcrumb("users", { isSuperadmin: me.is_superadmin }, [{ label: "Editar" }]),
    // Página cheia de criar/editar usuário — substitui o <Dialog> que vivia em
    // react/pages/usuarios/index.tsx. Mesmo padrão de rota de "perm-grupos"
    // logo abaixo: path regex sem grupo de captura, id re-parseado de
    // window.location.pathname pela própria página React.
    // ADR-014, Fase 0: breadcrumb vem do registry.
    chrome: "react" as const,
    render: (root: HTMLElement) => {
      const shell = {
        activeRoute: "perm-usuarios",
        breadcrumb: buildBreadcrumb("users", { isSuperadmin: me.is_superadmin }, [{ label: "Editar" }]),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
        listUrl: "/painel/admin/permissoes/usuarios/",
      };
      return import("./react/pages/usuarios/usuario-form").then((m) => m.renderUsuarioFormPage(root, shell));
    },
  },
  {
    path: /^\/painel\/admin\/permissoes\/perfis\/(novo|\d+\/editar)\/?$/,
    key: "perm-grupos",
    breadcrumb: buildBreadcrumb("permission-profiles", { isSuperadmin: me.is_superadmin }, [{ label: "Editar" }]),
    // Sunset ADR-009, Estágio 2, Lote 2 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/permissions/grupo-editor.ts) removido. Rota com path regex (sem
    // grupo de captura no router) — a página React re-parseia
    // window.location.pathname, mesma técnica do vanilla.
    // ADR-014, Fase 0: breadcrumb vem do registry.
    chrome: "react" as const,
    render: (root: HTMLElement) => {
      const shell = {
        activeRoute: "perm-grupos",
        breadcrumb: buildBreadcrumb("permission-profiles", { isSuperadmin: me.is_superadmin }, [{ label: "Editar" }]),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
        listUrl: "/painel/admin/permissoes/perfis/",
        isOperador: me.is_operador,
      };
      return import("./react/pages/grupo-editor").then((m) => m.renderGrupoEditorPage(root, shell));
    },
  },
  // Redirect legado: /permissoes/grupos/* → /permissoes/perfis/* (URL renomeada).
  defineInternalRedirect(
    /^\/painel\/admin\/permissoes\/grupos(\/.*)?$/,
    "perm-grupos",
    (path, search) => path.replace(/\/grupos(\/|$)/, "/perfis$1") + search,
  ),
  {
    path: "/painel/admin/permissoes/acesso/",
    key: "perm-acesso",
    breadcrumb: buildBreadcrumb("access-audit", { isSuperadmin: me.is_superadmin }),
    // Sunset ADR-009, Estágio 2, Lote 2 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/permissions/acesso-report.ts) removido.
    // ADR-014, Fase 0: breadcrumb vem do registry.
    chrome: "react" as const,
    render: (root: HTMLElement) => {
      const shell = {
        activeRoute: "perm-acesso",
        breadcrumb: buildBreadcrumb("access-audit", { isSuperadmin: me.is_superadmin }),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/acesso-report").then((m) => m.renderAcessoReportPage(root, shell));
    },
  },
  {
    path: "/painel/superadmin/permissoes/usuarios/",
    key: "perm-usuarios",
    breadcrumb: [
      { label: "Dashboard", href: "/painel/admin/" },
      { label: "Acessos & Permissões", href: "/painel/superadmin/permissoes/usuarios/" },
      { label: "Usuários" },
    ],
    // Sunset ADR-009, Estágio 2, Lote 6 — mesmo padrão da rota
    // "/painel/admin/permissoes/usuarios/" acima (arquivo Vanilla retido,
    // dependência de admin/usuarios.ts, Estágio 0 pendente).
    chrome: "react" as const,
    render: (root: HTMLElement) => {
      const shell = {
        activeRoute: "perm-usuarios",
        breadcrumb: [
          { label: "Dashboard", href: "/painel/admin/" },
          { label: "Acessos & Permissões", href: "/painel/superadmin/permissoes/usuarios/" },
          { label: "Usuários" },
        ],
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
        formBase: "/painel/superadmin/permissoes/usuarios/",
        isGestor: me.is_gestor,
        isOperador: me.is_operador,
      };
      return import("./react/pages/usuarios").then((m) => m.renderUsuariosPage(root, shell));
    },
  },
  {
    path: /^\/painel\/superadmin\/permissoes\/usuarios\/(novo|\d+\/editar)\/?$/,
    key: "perm-usuarios",
    breadcrumb: [
      { label: "Dashboard", href: "/painel/admin/" },
      { label: "Acessos & Permissões", href: "/painel/superadmin/permissoes/usuarios/" },
      { label: "Editar" },
    ],
    // Página cheia de criar/editar usuário — pareada com a rota de lista
    // "/painel/superadmin/permissoes/usuarios/" logo acima. Antes desta rota
    // não existir, "+ Novo"/"Editar" clicados a partir dessa zona caíam por
    // engano na zona "/painel/admin/permissoes/usuarios/" (achado do
    // reviewer, feature/usuarios-pagina-edicao-cadu).
    chrome: "react" as const,
    render: (root: HTMLElement) => {
      const shell = {
        activeRoute: "perm-usuarios",
        breadcrumb: [
          { label: "Dashboard", href: "/painel/admin/" },
          { label: "Acessos & Permissões", href: "/painel/superadmin/permissoes/usuarios/" },
          { label: "Editar" },
        ],
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
        listUrl: "/painel/superadmin/permissoes/usuarios/",
      };
      return import("./react/pages/usuarios/usuario-form").then((m) => m.renderUsuarioFormPage(root, shell));
    },
  },
  // Plano "Mídia e uploads por módulo" (Fase 5): a rota única de
  // "Customização da Instância" (ADR-042) virou 3 páginas (Instância/Layout/
  // White-label, `pages/configuracoes/`, Fase 6) + este redirect da URL
  // antiga — mesmo padrão de `defineInternalRedirect` já usado para
  // "/permissoes/grupos/" → "/permissoes/perfis/" acima. "/layout/" (não
  // "/instancia/") porque a página Layout absorve a maior parte da tela
  // antiga (paleta/tipografia/navegação).
  defineInternalRedirect(
    "/painel/superadmin/customizacao/",
    "instance-layout",
    (_path, search) => "/painel/superadmin/layout/" + search,
  ),
  {
    path: "/painel/superadmin/instancia/",
    key: "instance-settings",
    breadcrumb: buildBreadcrumb("instance-settings", { isSuperadmin: me.is_superadmin }),
    chrome: "react" as const,
    // ADR-042 (split Fase 6): página só de superadmin — mesmo padrão de guard
    // de "Documentação da API"/"Sitemap" acima.
    render: guardedSuperadmin((root: HTMLElement) => {
      const shell = {
        activeRoute: "instance-settings",
        breadcrumb: buildBreadcrumb("instance-settings", { isSuperadmin: me.is_superadmin }),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/configuracoes/instancia").then((m) => m.renderInstanceSettingsPage(root, shell));
    }),
  },
  {
    path: "/painel/superadmin/layout/",
    key: "instance-layout",
    breadcrumb: buildBreadcrumb("instance-layout", { isSuperadmin: me.is_superadmin }),
    chrome: "react" as const,
    render: guardedSuperadmin((root: HTMLElement) => {
      const shell = {
        activeRoute: "instance-layout",
        breadcrumb: buildBreadcrumb("instance-layout", { isSuperadmin: me.is_superadmin }),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/configuracoes/layout").then((m) => m.renderInstanceLayoutPage(root, shell));
    }),
  },
  {
    path: "/painel/superadmin/white-label/",
    key: "white-label",
    breadcrumb: buildBreadcrumb("white-label", { isSuperadmin: me.is_superadmin }),
    chrome: "react" as const,
    render: guardedSuperadmin((root: HTMLElement) => {
      const shell = {
        activeRoute: "white-label",
        breadcrumb: buildBreadcrumb("white-label", { isSuperadmin: me.is_superadmin }),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/configuracoes/white-label").then((m) => m.renderWhiteLabelPage(root, shell));
    }),
  },
  {
    path: "/painel/superadmin/mapa-site/",
    key: "sitemap",
    // Plano "Mídia e uploads por módulo" (Fase 5): breadcrumb literal →
    // buildBreadcrumb (registry ganhou a entrada "sitemap", renome de "Mapa
    // do Site" — URL mantida).
    breadcrumb: buildBreadcrumb("sitemap", { isSuperadmin: me.is_superadmin }),
    // Sunset ADR-009, Estágio 2, Lote 1 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/sitemap.ts) removido; rota resolve direto para a página React.
    chrome: "react" as const,
    // Mesma razão da rota de Documentação da API: guardada por superadmin,
    // em vez de depender só da visibilidade no menu.
    render: guardedSuperadmin((root: HTMLElement) => {
      const shell = {
        activeRoute: "sitemap",
        breadcrumb: buildBreadcrumb("sitemap", { isSuperadmin: me.is_superadmin }),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/sitemap").then((m) => m.renderSitemapPage(root, shell));
    }),
  },
  {
    path: "/painel/admin/midia/biblioteca/",
    key: "media-library",
    breadcrumb: buildBreadcrumb("media-library", { isSuperadmin: me.is_superadmin }),
    // Plano "Mídia e uploads por módulo" (Fase 5) — admin (perfil padrão) tem
    // a permissão por padrão (concedida via migração de dados, Fase 1); não é
    // `guardedSuperadmin`.
    chrome: "react" as const,
    render: guarded(codename("midia.admin.biblioteca", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "media-library",
        breadcrumb: buildBreadcrumb("media-library", { isSuperadmin: me.is_superadmin }),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/midia/biblioteca").then((m) => m.renderMediaLibraryPage(root, shell));
    }),
  },
  {
    path: "/painel/admin/midia/upload/",
    key: "media-upload",
    breadcrumb: buildBreadcrumb("media-upload", { isSuperadmin: me.is_superadmin }),
    chrome: "react" as const,
    render: guarded(codename("midia.admin.biblioteca", "create"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "media-upload",
        breadcrumb: buildBreadcrumb("media-upload", { isSuperadmin: me.is_superadmin }),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/midia/upload").then((m) => m.renderMediaUploadPage(root, shell));
    }),
  },
  {
    path: "/painel/superadmin/admin-templates/",
    key: "admin-templates",
    breadcrumb: [
      { label: "Dashboard", href: "/painel/admin/" },
      { label: "Perfis Admin", href: "/painel/superadmin/admin-templates/" },
      { label: "Templates de Perfil" },
    ],
    // Sunset ADR-009, Estágio 2, Lote 4 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/permissions/admin-templates.ts) removido.
    chrome: "react" as const,
    render: (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-templates",
        breadcrumb: [
          { label: "Dashboard", href: "/painel/admin/" },
          { label: "Perfis Admin", href: "/painel/superadmin/admin-templates/" },
          { label: "Templates de Perfil" },
        ],
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/admin-templates").then((m) => m.renderAdminTemplatesPage(root, shell));
    },
  },
  {
    path: "/painel/admin/admin-modulos/",
    key: "admin-modulos",
    breadcrumb: [
      { label: "Dashboard", href: "/painel/admin/" },
      { label: "Gerenciar Admins" },
    ],
    // Sunset ADR-009, Estágio 2, Lote 6 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/permissions/admin-modulos.ts) removido. Gate funcional (Onda 3
    // pág. 13) e de dependências cruzadas confirmados limpos.
    // Fase 1 do ADR-012 (react-router, escopo pleno) — 1º lote real migrado
    // para <Route> sob o mecanismo do Spike B (react/router-bridge.tsx).
    chrome: "react" as const,
    render: (root: HTMLElement) => {
      const shell = {
        zone: "superadmin" as const,
        activeRoute: "admin-modulos",
        breadcrumb: [
          { label: "Dashboard", href: "/painel/admin/" },
          { label: "Gerenciar Admins" },
        ],
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/router-bridge").then((m) => m.renderReactRouterLocation(root, sharedHistory, shell));
    },
  },
  // ── Admin area ─────────────────────────────────────────────────────────
  {
    path: "/painel/admin/",
    key: "admin-dashboard",
    // Página raiz do zone admin — sem breadcrumb (nada acima de "Dashboard"
    // para subir), mesmo padrão do zone user (dashboard sem trilha).
    breadcrumb: [],
    // Sunset ADR-009, Estágio 2, Lote 2 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/admin/dashboard.ts) removido.
    chrome: "react" as const,
    render: (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-dashboard",
        breadcrumb: [],
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/admin-dashboard").then((m) => m.renderAdminDashboardPage(root, shell));
    },
  },
  {
    path: "/painel/admin/empreendimentos/",
    key: "admin-empreendimentos",
    breadcrumb: buildBreadcrumb("admin-empreendimentos", { isSuperadmin: me.is_superadmin }),
    // Sunset ADR-009, Estágio 2, Lote 5 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/empreendimentos.ts) removido. guarded(codename) preservado 1:1 —
    // mesmo codename usado pela rota de perfil (já React).
    // ADR-014, Fase 3, Implementação: breadcrumb vem do registry.
    chrome: "react" as const,
    render: guarded(codename("parametrizacoes.admin.empreendimentos", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-empreendimentos",
        breadcrumb: buildBreadcrumb("admin-empreendimentos", { isSuperadmin: me.is_superadmin }),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/admin-empreendimentos").then((m) => m.renderAdminEmpreendimentosPage(root, shell));
    }),
  },
  {
    path: /^\/painel\/admin\/empreendimentos\/\d+\/perfil(?:\/(informacoes|apresentacao|comodidades|localizacao|preview)?)?\/?$/,
    key: "admin-empreendimentos",
    breadcrumb: buildBreadcrumb("admin-empreendimentos", { isSuperadmin: me.is_superadmin }, [
      { label: "Enriquecimento do Perfil" },
    ]),
    // Onda 3, Bloco C, página 18 (última) — ADR-008. Ver docs/migracao-react/
    // ONDA-3-preparacao-critica-pagina-18-empreendimentos-perfil.md.
    // Sunset ADR-009, Estágio 2, Lote 7 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Bloco condicional
    // removido (dead code desde o Estágio 1).
    // ADR-014, Fase 3, Implementação: breadcrumb vem do registry (`extra`
    // para o segmento final, mesmo padrão da rota de edição de grupo).
    chrome: "react" as const,
    render: guarded(codename("parametrizacoes.admin.empreendimentos", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-empreendimentos",
        breadcrumb: buildBreadcrumb("admin-empreendimentos", { isSuperadmin: me.is_superadmin }, [
          { label: "Enriquecimento do Perfil" },
        ]),
        zone: "superadmin" as const,
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/empreendimentos-perfil").then((m) => m.renderEmpreendimentoPerfilPage(root, shell));
    }),
  },
  {
    path: "/painel/admin/usuarios/",
    key: "admin-usuarios",
    breadcrumb: buildBreadcrumb("users", { isSuperadmin: me.is_superadmin }),
    // Sunset ADR-009, Estágio 0 — última migração de página do ADR-009. Ver
    // docs/migracao-react/SUNSET-ESTAGIO-0-admin-usuarios-preparacao.md.
    // Reaproveita react/pages/usuarios.tsx (já existente), estendido com
    // allowedPermCodes/hideSuperadminOption — os 2 comportamentos que só
    // `pages/admin/usuarios.ts` (removido) adicionava sobre a página
    // compartilhada.
    // ADR-014, Fase 0: breadcrumb vem do registry.
    chrome: "react" as const,
    render: guarded(codename("gestao.admin.usuarios", "view"), async (root: HTMLElement) => {
      const res = await api
        .get<{ results?: { codename: string }[] } | { codename: string }[]>("/api/permissions/features/available/")
        .catch((): { codename: string }[] => []);
      const list = Array.isArray(res) ? res : (res.results ?? []);
      const allowedPermCodes = new Set(list.map((f) => f.codename));
      const shell = {
        activeRoute: "admin-usuarios",
        breadcrumb: buildBreadcrumb("users", { isSuperadmin: me.is_superadmin }),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
        isGestor: me.is_gestor,
        isOperador: me.is_operador,
        allowedPermCodes,
        hideSuperadminOption: true,
        formBase: "/painel/admin/usuarios/",
      };
      return import("./react/pages/usuarios").then((m) => m.renderUsuariosPage(root, shell));
    }),
  },
  {
    path: /^\/painel\/admin\/usuarios\/(novo|\d+\/editar)\/?$/,
    key: "admin-usuarios",
    breadcrumb: buildBreadcrumb("users", { isSuperadmin: me.is_superadmin }, [{ label: "Editar" }]),
    // Página cheia de criar/editar usuário — substitui o <Dialog> que vivia em
    // react/pages/usuarios/index.tsx. Mesmo padrão de rota de "admin-grupos"
    // logo abaixo: path regex sem grupo de captura, id re-parseado de
    // window.location.pathname pela própria página React. guarded(codename)
    // preservado 1:1 com a rota de listagem acima.
    // ADR-014, Fase 0: breadcrumb vem do registry.
    chrome: "react" as const,
    render: guarded(codename("gestao.admin.usuarios", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-usuarios",
        breadcrumb: buildBreadcrumb("users", { isSuperadmin: me.is_superadmin }, [{ label: "Editar" }]),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
        hideSuperadminOption: true,
        listUrl: "/painel/admin/usuarios/",
      };
      return import("./react/pages/usuarios/usuario-form").then((m) => m.renderUsuarioFormPage(root, shell));
    }),
  },
  {
    path: "/painel/admin/perfis/",
    key: "admin-grupos",
    breadcrumb: buildBreadcrumb("permission-profiles", { isSuperadmin: me.is_superadmin }),
    // Sunset ADR-009, Estágio 2, Lote 2 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/admin/grupos.ts, wrapper) removido. guarded(codename)
    // preservado 1:1.
    // ADR-014, Fase 0: breadcrumb vem do registry.
    chrome: "react" as const,
    render: guarded(codename("gestao.admin.grupos", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-grupos",
        breadcrumb: buildBreadcrumb("permission-profiles", { isSuperadmin: me.is_superadmin }),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
        isOperador: me.is_operador,
      };
      return import("./react/pages/grupos").then((m) => m.renderGruposPage(root, shell));
    }),
  },
  {
    path: "/painel/admin/acesso/",
    key: "admin-acesso",
    breadcrumb: buildBreadcrumb("access-audit", { isSuperadmin: me.is_superadmin }),
    // Sunset ADR-009, Estágio 2, Lote 2 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/permissions/acesso-report.ts) removido. guarded(codename)
    // preservado 1:1.
    // ADR-014, Fase 0: breadcrumb vem do registry.
    // Fase 1 do ADR-012 (react-router, escopo pleno) — 1º lote real migrado
    // para <Route> sob o mecanismo do Spike B (react/router-bridge.tsx). Só
    // esta rota (não "perm-acesso" abaixo, double-mounted na mesma página)
    // entrou no lote — a outra continua no dispatch legado sem problema,
    // ambas reaproveitam o mesmo `AcessoReportPage` exportado.
    chrome: "react" as const,
    render: guarded(codename("gestao.admin.grupos", "view"), (root: HTMLElement) => {
      const shell = {
        zone: "superadmin" as const,
        activeRoute: "admin-acesso",
        breadcrumb: buildBreadcrumb("access-audit", { isSuperadmin: me.is_superadmin }),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/router-bridge").then((m) => m.renderReactRouterLocation(root, sharedHistory, shell));
    }),
  },
  {
    path: /^\/painel\/admin\/perfis\/(novo|\d+\/editar)\/?$/,
    key: "admin-grupos",
    breadcrumb: buildBreadcrumb("permission-profiles", { isSuperadmin: me.is_superadmin }, [{ label: "Editar" }]),
    // Sunset ADR-009, Estágio 2, Lote 2 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/permissions/grupo-editor.ts) removido. guarded(codename)
    // preservado 1:1.
    // ADR-014, Fase 0: breadcrumb vem do registry.
    chrome: "react" as const,
    render: guarded(codename("gestao.admin.grupos", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-grupos",
        breadcrumb: buildBreadcrumb("permission-profiles", { isSuperadmin: me.is_superadmin }, [{ label: "Editar" }]),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
        listUrl: "/painel/admin/perfis/",
        isOperador: me.is_operador,
      };
      return import("./react/pages/grupo-editor").then((m) => m.renderGrupoEditorPage(root, shell));
    }),
  },
  // Redirect legado: /admin/grupos/* → /admin/perfis/* (URL renomeada).
  defineInternalRedirect(
    /^\/painel\/admin\/grupos(\/.*)?$/,
    "admin-grupos",
    (path, search) => path.replace(/\/grupos(\/|$)/, "/perfis$1") + search,
  ),
  {
    path: "/painel/admin/catalogo/",
    key: "admin-catalogo",
    breadcrumb: buildBreadcrumb("permission-catalog", { isSuperadmin: me.is_superadmin }),
    // Onda 3, Bloco C, página 11 (ADR-008), guarded(codename) preservado 1:1.
    // Double-mounted com "perm-catalogo". Sunset ADR-009, Estágio 2, Lote 6-B —
    // ver docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Bloco
    // condicional removido (dead code desde o Estágio 1).
    // ADR-014, Fase 0: breadcrumb vem do registry — mesmo id de "perm-catalogo"
    // acima, o que corrige de fato a divergência de nome entre as 2 rotas
    // double-mounted (achado P1 da auditoria de navegação).
    chrome: "react" as const,
    render: guarded(codename("gestao.admin.catalogo", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-catalogo",
        breadcrumb: buildBreadcrumb("permission-catalog", { isSuperadmin: me.is_superadmin }),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/catalogo").then((m) => m.renderCatalogoPage(root, shell));
    }),
  },
  {
    path: "/painel/admin/catalogo/editar/",
    key: "admin-catalogo-editar",
    breadcrumb: buildBreadcrumb("permission-catalog", { isSuperadmin: me.is_superadmin }, [{ label: "Permissão" }]),
    // Double-mounted com "perm-catalogo-editar" acima (mesma nota sobre
    // action=newpermission vs. categoria/subcategoria/nome). guarded() espelha
    // a rota de lista correspondente ("admin-catalogo"), diferente do mount
    // "/permissoes/catalogo/editar/" (sem guarded, mesma assimetria já
    // existente entre as 2 rotas de lista).
    chrome: "react" as const,
    render: guarded(codename("gestao.admin.catalogo", "view"), (root: HTMLElement) => {
      const isCreate = new URLSearchParams(window.location.search).get("action") === "newpermission";
      const shell = {
        activeRoute: "admin-catalogo-editar",
        breadcrumb: buildBreadcrumb("permission-catalog", { isSuperadmin: me.is_superadmin }, [
          { label: isCreate ? "Nova Permissão" : "Editar Permissão" },
        ]),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
        listUrl: "/painel/admin/catalogo/",
      };
      return import("./react/pages/catalogo-editar").then((m) => m.renderCatalogoEditarPage(root, shell));
    }),
  },
  defineComingSoonRoute(
    "/painel/admin/permissoes/",
    "admin-permissoes",
    "Permissões",
    [{ label: "Dashboard", href: "/painel/admin/" }, { label: "Permissões" }],
  ),
  {
    path: "/painel/admin/reservas/",
    key: "admin-reservas",
    breadcrumb: [{ label: "Dashboard", href: "/painel/admin/" }, { label: "Reservas" }],
    chrome: "react" as const,
    render: guarded(codename("hotelaria.admin.reservas", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-reservas",
        breadcrumb: [{ label: "Dashboard", href: "/painel/admin/" }, { label: "Reservas" }],
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/admin-reservas").then((m) => m.renderAdminReservasPage(root, shell));
    }),
  },
  defineComingSoonRoute(
    "/painel/admin/ocupacao/",
    "admin-ocupacao",
    "Ocupação",
    [{ label: "Dashboard", href: "/painel/admin/" }, { label: "Ocupação" }],
  ),
  {
    path: "/painel/admin/pool/",
    key: "admin-pool",
    breadcrumb: [{ label: "Dashboard", href: "/painel/admin/" }, { label: "Gerenciamento" }, { label: "Pool Hoteleiro" }],
    // Sunset ADR-009, Estágio 2, Lote 4 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/admin/pool-aprovacao.ts) removido. guarded(codename) preservado 1:1.
    chrome: "react" as const,
    render: guarded(codename("parametrizacoes.admin.pool-aprovacao", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-pool",
        breadcrumb: [{ label: "Dashboard", href: "/painel/admin/" }, { label: "Gerenciamento" }, { label: "Pool Hoteleiro" }],
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/admin-pool-aprovacao").then((m) => m.renderAdminPoolAprovacaoPage(root, shell));
    }),
  },
  {
    path: "/painel/admin/emails/",
    key: "admin-emails",
    breadcrumb: [{ label: "Dashboard", href: "/painel/admin/" }, { label: "Gerenciamento" }, { label: "Disparos de E-mail" }],
    // Sunset ADR-009, Estágio 2, Lote 2 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/admin/emails.ts) removido. guarded(codename) preservado 1:1.
    chrome: "react" as const,
    render: guarded(codename("gerenciamento.admin.emails", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-emails",
        breadcrumb: [{ label: "Dashboard", href: "/painel/admin/" }, { label: "Gerenciamento" }, { label: "Disparos de E-mail" }],
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/admin-emails").then((m) => m.renderAdminEmailsPage(root, shell));
    }),
  },
  {
    path: "/painel/admin/remetentes-email/",
    key: "admin-remetentes-email",
    breadcrumb: [{ label: "Dashboard", href: "/painel/admin/" }, { label: "Gerenciamento" }, { label: "Remetentes de E-mail" }],
    // Migração React MUI (ADR-011) — fallback Vanilla (pages/admin/remetentes-email.ts)
    // removido. Era a última rota sem `chrome: "react"`: montava no chrome
    // antigo e não renderizava depois do merge develop → dev-teste.
    chrome: "react" as const,
    render: guarded(codename("gerenciamento.admin.remetentes-email", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-remetentes-email",
        breadcrumb: [{ label: "Dashboard", href: "/painel/admin/" }, { label: "Gerenciamento" }, { label: "Remetentes de E-mail" }],
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/admin-remetentes-email").then((m) => m.renderAdminRemetentesEmailPage(root, shell));
    }),
  },
  {
    path: "/painel/admin/comunicados/",
    key: "admin-comunicados",
    breadcrumb: [{ label: "Dashboard", href: "/painel/admin/" }, { label: "Gerenciamento" }, { label: "Comunicados" }],
    // Migração React MUI (ADR-011) — fallback Vanilla (pages/admin/comunicados.ts) removido.
    chrome: "react" as const,
    render: guarded(codename("gerenciamento.admin.comunicados", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-comunicados",
        breadcrumb: [{ label: "Dashboard", href: "/painel/admin/" }, { label: "Gerenciamento" }, { label: "Comunicados" }],
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/admin-comunicados").then((m) => m.renderAdminComunicadosPage(root, shell));
    }),
  },
  {
    path: /^\/painel\/admin\/comunicados\/relatorio\/?$/,
    key: "admin-comunicados",
    breadcrumb: [
      { label: "Dashboard", href: "/painel/admin/" },
      { label: "Gerenciamento" },
      { label: "Comunicados", href: "/painel/admin/comunicados/" },
      { label: "Publicados" },
    ],
    chrome: "react" as const,
    render: guarded(codename("gerenciamento.admin.comunicados", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-comunicados",
        breadcrumb: [
          { label: "Dashboard", href: "/painel/admin/" },
          { label: "Gerenciamento" },
          { label: "Comunicados", href: "/painel/admin/comunicados/" },
          { label: "Publicados" },
        ],
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/admin-comunicados").then((m) => m.renderRelatorioComunicadosPage(root, shell));
    }),
  },
  {
    path: /^\/painel\/admin\/comunicados\/novo\/?$/,
    key: "admin-comunicado-form",
    breadcrumb: [
      { label: "Dashboard", href: "/painel/admin/" },
      { label: "Gerenciamento" },
      { label: "Comunicados", href: "/painel/admin/comunicados/" },
      { label: "Novo Comunicado" },
    ],
    // Migração React MUI (ADR-011) — fallback Vanilla (pages/admin/comunicado-form.ts) removido.
    chrome: "react" as const,
    render: guarded(codename("gerenciamento.admin.comunicados", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-comunicados",
        breadcrumb: [
          { label: "Dashboard", href: "/painel/admin/" },
          { label: "Gerenciamento" },
          { label: "Comunicados", href: "/painel/admin/comunicados/" },
          { label: "Novo Comunicado" },
        ],
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/admin-comunicado-form").then((m) => m.renderComunicadoFormPage(root, shell));
    }),
  },
  {
    path: /^\/painel\/admin\/comunicados\/\d+\/editar\/?$/,
    key: "admin-comunicado-form",
    breadcrumb: [
      { label: "Dashboard", href: "/painel/admin/" },
      { label: "Gerenciamento" },
      { label: "Comunicados", href: "/painel/admin/comunicados/" },
      { label: "Editar Comunicado" },
    ],
    // Migração React MUI (ADR-011) — fallback Vanilla (pages/admin/comunicado-form.ts) removido.
    chrome: "react" as const,
    render: guarded(codename("gerenciamento.admin.comunicados", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-comunicados",
        breadcrumb: [
          { label: "Dashboard", href: "/painel/admin/" },
          { label: "Gerenciamento" },
          { label: "Comunicados", href: "/painel/admin/comunicados/" },
          { label: "Editar Comunicado" },
        ],
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/admin-comunicado-form").then((m) => m.renderComunicadoFormPage(root, shell));
    }),
  },
  {
    path: "/painel/admin/documentos/",
    key: "admin-documentos",
    breadcrumb: [{ label: "Dashboard", href: "/painel/admin/" }, { label: "Gerenciamento" }, { label: "Documentos" }],
    chrome: "react" as const,
    render: guarded(codename("gerenciamento.admin.documentos", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-documentos",
        breadcrumb: [{ label: "Dashboard", href: "/painel/admin/" }, { label: "Gerenciamento" }, { label: "Documentos" }],
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/admin-documentos").then((m) => m.renderAdminDocumentosPage(root, shell));
    }),
  },
  {
    path: "/painel/admin/documentos-categorias/",
    key: "admin-documentos-categorias",
    breadcrumb: buildBreadcrumb("admin-documentos-categorias", { isSuperadmin: me.is_superadmin }),
    // Extraído da rota "/painel/admin/documentos/" (que antes cobria Documentos
    // e Categorias em 2 abas sob um guard resource-any) — permissão própria,
    // sem OR com `gerenciamento.admin.documentos` (Card 868m1ja6b).
    chrome: "react" as const,
    render: guarded(codename("parametrizacoes.admin.documentos-categorias", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-documentos-categorias",
        breadcrumb: buildBreadcrumb("admin-documentos-categorias", { isSuperadmin: me.is_superadmin }),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/admin-documentos-categorias").then((m) =>
        m.renderAdminDocumentosCategoriasPage(root, shell),
      );
    }),
  },
  {
    path: /^\/painel\/admin\/documentos\/novo\/?$/,
    key: "admin-documento-form",
    breadcrumb: [
      { label: "Dashboard", href: "/painel/admin/" },
      { label: "Gerenciamento" },
      { label: "Documentos", href: "/painel/admin/documentos/" },
      { label: "Novo Documento" },
    ],
    chrome: "react" as const,
    render: guarded(codename("gerenciamento.admin.documentos", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-documento-form",
        breadcrumb: [
          { label: "Dashboard", href: "/painel/admin/" },
          { label: "Gerenciamento" },
          { label: "Documentos", href: "/painel/admin/documentos/" },
          { label: "Novo Documento" },
        ],
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/admin-documento-form").then((m) => m.renderDocumentoFormPage(root, shell));
    }),
  },
  {
    path: /^\/painel\/admin\/documentos\/(\d+)\/editar\/?$/,
    key: "admin-documento-form",
    breadcrumb: [
      { label: "Dashboard", href: "/painel/admin/" },
      { label: "Gerenciamento" },
      { label: "Documentos", href: "/painel/admin/documentos/" },
      { label: "Editar Documento" },
    ],
    chrome: "react" as const,
    render: guarded(codename("gerenciamento.admin.documentos", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-documento-form",
        breadcrumb: [
          { label: "Dashboard", href: "/painel/admin/" },
          { label: "Gerenciamento" },
          { label: "Documentos", href: "/painel/admin/documentos/" },
          { label: "Editar Documento" },
        ],
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/admin-documento-form").then((m) => m.renderDocumentoFormPage(root, shell));
    }),
  },
  defineComingSoonRoute(
    "/painel/admin/leads/",
    "admin-leads",
    "Leads",
    [{ label: "Dashboard", href: "/painel/admin/" }, { label: "Leads" }],
  ),
  defineComingSoonRoute(
    "/painel/admin/marketing/",
    "admin-marketing",
    "Marketing",
    [{ label: "Dashboard", href: "/painel/admin/" }, { label: "Marketing" }],
  ),
  {
    path: "/painel/admin/integracoes/",
    key: "integracoes",
    breadcrumb: buildBreadcrumb("integrations", { isSuperadmin: me.is_superadmin }),
    // Sunset ADR-009, Estágio 2, Lote 6 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/integracoes.ts) removido. Gate funcional (Onda 3 pág. 15) e de
    // dependências cruzadas confirmados limpos.
    // ADR-014, Fase 3, Implementação: breadcrumb vem do registry (fecha a
    // inconsistência encontrada no PoC A — Launcher, sidebar e breadcrumb
    // agora leem o mesmo "integrations").
    // Fase 1 do ADR-012 (react-router, escopo pleno) — 1º lote real migrado
    // para <Route> sob o mecanismo do Spike B (react/router-bridge.tsx).
    chrome: "react" as const,
    render: (root: HTMLElement) => {
      const shell = {
        zone: "superadmin" as const,
        activeRoute: "integracoes",
        breadcrumb: buildBreadcrumb("integrations", { isSuperadmin: me.is_superadmin }),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/router-bridge").then((m) => m.renderReactRouterLocation(root, sharedHistory, shell));
    },
  },
  // Formulário de Integração (criar/editar) — rota própria em vez de Dialog
  // por cima da listagem (pedido de 29-09-2026; mesmo padrão de Documentos,
  // `/documentos/novo/` e `/documentos/{id}/editar/`). Mesmo mecanismo da
  // listagem (react-router via router-bridge.tsx, ADR-012): o `<Route>` com
  // `:id` vive lá; aqui só o match do dispatcher legado + breadcrumb (crumb
  // "Integrações" vira link por causa do `extra`). `integracao-form` está em
  // `routeKeys` de "integrations" (nav-registry) para a sidebar manter
  // "Integrações" ativa nas duas rotas.
  {
    path: /^\/painel\/admin\/integracoes\/nova\/?$/,
    key: "integracao-form",
    breadcrumb: buildBreadcrumb("integrations", { isSuperadmin: me.is_superadmin }, [{ label: "Nova Integração" }]),
    chrome: "react" as const,
    render: (root: HTMLElement) => {
      const shell = {
        zone: "superadmin" as const,
        activeRoute: "integracao-form",
        breadcrumb: buildBreadcrumb("integrations", { isSuperadmin: me.is_superadmin }, [{ label: "Nova Integração" }]),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/router-bridge").then((m) => m.renderReactRouterLocation(root, sharedHistory, shell));
    },
  },
  {
    path: /^\/painel\/admin\/integracoes\/(\d+)\/editar\/?$/,
    key: "integracao-form",
    breadcrumb: buildBreadcrumb("integrations", { isSuperadmin: me.is_superadmin }, [{ label: "Editar Integração" }]),
    chrome: "react" as const,
    render: (root: HTMLElement) => {
      const shell = {
        zone: "superadmin" as const,
        activeRoute: "integracao-form",
        breadcrumb: buildBreadcrumb("integrations", { isSuperadmin: me.is_superadmin }, [{ label: "Editar Integração" }]),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/router-bridge").then((m) => m.renderReactRouterLocation(root, sharedHistory, shell));
    },
  },
  {
    path: "/painel/admin/boletos/",
    key: "admin-boletos",
    breadcrumb: buildBreadcrumb("admin-boletos", { isSuperadmin: me.is_superadmin }),
    // Sunset ADR-009, Estágio 2, Lote 4 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/admin/boletos.ts) removido. guarded(codename) preservado 1:1.
    chrome: "react" as const,
    render: guarded(codename("parametrizacoes.admin.boletos", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-boletos",
        breadcrumb: buildBreadcrumb("admin-boletos", { isSuperadmin: me.is_superadmin }),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/admin-boletos").then((m) => m.renderAdminBoletosPage(root, shell));
    }),
  },
  {
    path: "/painel/admin/portal-regras/",
    key: "admin-portal-regras",
    breadcrumb: buildBreadcrumb("admin-portal-regras", { isSuperadmin: me.is_superadmin }),
    // Sunset ADR-009, Estágio 2, Lote 6 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/admin/portal-regras.ts) removido. guarded(codename) preservado
    // 1:1. Gate funcional (Onda 3 pág. 16) e de dependências cruzadas
    // confirmados limpos.
    chrome: "react" as const,
    render: guarded(codename("parametrizacoes.admin.portal-regras", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-portal-regras",
        breadcrumb: buildBreadcrumb("admin-portal-regras", { isSuperadmin: me.is_superadmin }),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/portal-regras").then((m) => m.renderPortalRegrasPage(root, shell));
    }),
  },
  {
    path: /^\/painel\/admin\/portal-regras\/\d+\/(reservas|pool|financeiro|limites|documento|termos|param)?\/?$/,
    key: "admin-portal-regras",
    breadcrumb: buildBreadcrumb("admin-portal-regras", { isSuperadmin: me.is_superadmin }, [{ label: "Empreendimento" }]),
    chrome: "react" as const,
    render: guarded(codename("parametrizacoes.admin.portal-regras", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "admin-portal-regras",
        breadcrumb: buildBreadcrumb("admin-portal-regras", { isSuperadmin: me.is_superadmin }, [{ label: "Empreendimento" }]),
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/portal-regras").then((m) => m.renderPortalRegrasDetalhePage(root, shell));
    }),
  },
  {
    path: "/painel/admin/perfil/",
    key: "admin-perfil",
    breadcrumb: [{ label: "Dashboard", href: "/painel/admin/" }, { label: "Meu Perfil" }],
    // Sunset ADR-009, Estágio 2, Lote 2 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/admin/perfil.ts) removido.
    // Fase 1 do ADR-012 (react-router, escopo pleno) — 1º lote real migrado
    // para <Route> sob o mecanismo do Spike B (react/router-bridge.tsx).
    chrome: "react" as const,
    render: (root: HTMLElement) => {
      const shell = {
        zone: "superadmin" as const,
        activeRoute: "admin-perfil",
        breadcrumb: [{ label: "Dashboard", href: "/painel/admin/" }, { label: "Meu Perfil" }],
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/router-bridge").then((m) => m.renderReactRouterLocation(root, sharedHistory, shell));
    },
  },
  defineComingSoonRoute(
    "/painel/admin/financeiro/",
    "admin-financeiro",
    "Financeiro",
    [{ label: "Dashboard", href: "/painel/admin/" }, { label: "Financeiro" }],
  ),
  defineComingSoonRoute(
    "/painel/admin/relatorios/",
    "admin-relatorios",
    "Relatórios",
    [{ label: "Dashboard", href: "/painel/admin/" }, { label: "Relatórios" }],
  ),
  // Plano "Mídia e uploads por módulo" (Fase 5): placeholder "Em breve" sem
  // link de nav (nenhum item de sidebar aponta pra cá) virou redirect para a
  // 1ª página real do grupo "Configurações" — mesmo padrão do redirect de
  // "/painel/superadmin/customizacao/" acima.
  defineInternalRedirect(
    "/painel/admin/configuracoes/",
    "integracoes",
    (_path, search) => "/painel/admin/integracoes/" + search,
  ),
  // Rota de prova de conceito das Fases 1-3 da migração React+MUI (ADR-008) —
  // sem link na navegação, só acessível a admin/superadmin pelo guard de
  // /painel/* já aplicado em mountChrome. chrome:"react" (Fase 3, Estágio 2):
  // a própria rota desenha sidebar+header via DashboardLayout. Ver
  // docs/migracao-react/FASE-3-plano-tecnico.md.
  {
    path: "/painel/admin/_react-poc/",
    key: "react-poc",
    breadcrumb: [{ label: "Dashboard", href: "/painel/admin/" }, { label: "React PoC" }],
    chrome: "react" as const,
    render: (root: HTMLElement) => {
      const shell = {
        activeRoute: "react-poc",
        breadcrumb: [{ label: "Dashboard", href: "/painel/admin/" }, { label: "React PoC" }],
        zone: "superadmin" as const,
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/PocPage").then((m) => m.renderPocPage(root, shell));
    },
  },
  // ── User area ───────────────────────────────────────────────────────────
  // Codenames match the seed: {modulo}.{pagina}.access
  // ── Cotista — funcionalidades gerais ───────────────────────────────────
  {
    path: /^\/app\/dashboard\/?$/,
    key: "user-dashboard",
    breadcrumb: [],
    // Sunset ADR-009, Estágio 2, Lote 3 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/user/dashboard.ts) removido. guarded(codename) preservado 1:1.
    chrome: "react" as const,
    render: guarded(codename("cotista.user.dashboard", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "user-dashboard",
        breadcrumb: [],
        zone: "user" as const,
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/dashboard").then((m) => m.renderDashboardPage(root, shell));
    }),
  },
  {
    path: /^\/app\/empreendimentos\/?$/,
    key: "user-empreendimentos",
    breadcrumb: [{ label: "Dashboard", href: "/app/dashboard" }, { label: "Meus Empreendimentos" }],
    // Sunset ADR-009, Estágio 2, Lote 3 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/user/empreendimentos.ts) removido. guarded(codename) preservado 1:1.
    chrome: "react" as const,
    render: guarded(codename("cotista.user.empreendimentos", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "user-empreendimentos",
        breadcrumb: [{ label: "Dashboard", href: "/app/dashboard" }, { label: "Meus Empreendimentos" }],
        zone: "user" as const,
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/empreendimentos").then((m) => m.renderEmpreendimentosPage(root, shell));
    }),
  },
  {
    path: /^\/app\/empreendimentos\/(\d+)\/?$/,
    key: "user-empreendimento-detalhe",
    breadcrumb: [
      { label: "Dashboard", href: "/app/dashboard" },
      { label: "Meus Empreendimentos", href: "/app/empreendimentos" },
      { label: "Detalhe" },
    ],
    // Sunset ADR-009, Estágio 2, Lote 3 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/user/empreendimento-detalhe.ts) removido. guarded(codename)
    // preservado 1:1.
    chrome: "react" as const,
    render: guarded(codename("cotista.user.empreendimentos", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "user-empreendimento-detalhe",
        breadcrumb: [
          { label: "Dashboard", href: "/app/dashboard" },
          { label: "Meus Empreendimentos", href: "/app/empreendimentos" },
          { label: "Detalhe" },
        ],
        zone: "user" as const,
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/empreendimento-detalhe").then((m) => m.renderEmpreendimentoDetalhePage(root, shell));
    }),
  },
  // 04-09-2026 (encerramento da ADR-029) — "Pool Hoteleiro" virou "Em breve":
  // decisão do dono de não manter dado mockado (RECEITA_POR_EMPREENDIMENTO
  // etc.) em produção. path/key/breadcrumb/permissão preservados 1:1.
  defineComingSoonRoute(
    /^\/app\/pool\/?$/,
    "user-pool",
    "Pool Hoteleiro",
    buildBreadcrumb("pool-visao-geral", { isSuperadmin: me.is_superadmin }),
    { zone: "user", permission: codename("hotelaria.user.pool-hoteleiro", "view") },
  ),
  {
    path: /^\/app\/cotas\/?$/,
    key: "user-cotas",
    breadcrumb: buildBreadcrumb("pool-cotas-unidades", { isSuperadmin: me.is_superadmin }),
    // Sunset ADR-009, Estágio 2, Lote 3 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/user/cotas.ts) removido. guarded(codename) preservado 1:1.
    chrome: "react" as const,
    render: guarded(codename("condominio.user.cotas", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "user-cotas",
        breadcrumb: buildBreadcrumb("pool-cotas-unidades", { isSuperadmin: me.is_superadmin }),
        zone: "user" as const,
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/cotas").then((m) => m.renderCotasPage(root, shell));
    }),
  },
  // 04-09-2026 (encerramento da ADR-029) — "Ocupação" virou "Em breve":
  // decisão do dono de não manter dado mockado em produção. path/key/
  // breadcrumb/permissão preservados 1:1.
  defineComingSoonRoute(
    /^\/app\/ocupacao\/?$/,
    "user-ocupacao",
    "Ocupação",
    buildBreadcrumb("pool-ocupacao", { isSuperadmin: me.is_superadmin }),
    { zone: "user", permission: codename("hotelaria.user.ocupacao", "view") },
  ),
  // 04-09-2026 (encerramento da ADR-029) — "Condomínio" virou "Em breve":
  // decisão do dono de não manter dado mockado em produção. path/key/
  // breadcrumb/permissão preservados 1:1.
  defineComingSoonRoute(
    /^\/app\/condominio\/?$/,
    "user-condominio",
    "Condomínio",
    [{ label: "Dashboard", href: "/app/dashboard" }, { label: "Condomínio" }],
    { zone: "user", permission: codename("condominio.user.condominio", "view") },
  ),
  // 04-09-2026 (encerramento da ADR-029) — "Prestação de Contas" virou "Em
  // breve": decisão do dono de não manter dado mockado em produção. path/key/
  // breadcrumb/permissão preservados 1:1.
  defineComingSoonRoute(
    /^\/app\/prestacao-contas\/?$/,
    "user-prestacao",
    "Prestação de Contas",
    [{ label: "Dashboard", href: "/app/dashboard" }, { label: "Prestação de Contas" }],
    { zone: "user", permission: codename("condominio.user.prestacao-contas", "view") },
  ),
  {
    path: /^\/app\/reservas\/?$/,
    key: "user-reservas",
    breadcrumb: [{ label: "Dashboard", href: "/app/dashboard" }, { label: "Minhas Reservas" }],
    // Onda 3, Bloco C, página 17 (ADR-008 — ver docs/migracao-react/
    // ONDA-3-preparacao-critica-pagina-17-reservas.md). Sunset ADR-009,
    // Estágio 2, Lote 7 — ver docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md.
    // Bloco condicional removido (dead code desde o Estágio 1).
    //
    // Split 13-08-2026: esta rota vira a LISTAGEM ("Minhas Reservas",
    // `minhas-reservas.tsx`) — o wizard de criação migrou para
    // `/app/reservas/nova` (`reservas.tsx`, mesma key de permissão).
    chrome: "react" as const,
    render: guarded(codename("hotelaria.user.reservas", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "user-reservas",
        breadcrumb: [{ label: "Dashboard", href: "/app/dashboard" }, { label: "Minhas Reservas" }],
        zone: "user" as const,
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/minhas-reservas").then((m) => m.renderMinhasReservasPage(root, shell));
    }),
  },
  {
    path: /^\/app\/reservas\/nova\/?$/,
    key: "user-reservas-nova",
    breadcrumb: [
      { label: "Dashboard", href: "/app/dashboard" },
      { label: "Minhas Reservas", href: "/app/reservas" },
      { label: "Realizar Reserva" },
    ],
    chrome: "react" as const,
    render: guarded(codename("hotelaria.user.reservas", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "user-reservas-nova",
        breadcrumb: [
          { label: "Dashboard", href: "/app/dashboard" },
          { label: "Minhas Reservas", href: "/app/reservas" },
          { label: "Realizar Reserva" },
        ],
        zone: "user" as const,
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/reservas").then((m) => m.renderReservasPage(root, shell));
    }),
  },
  {
    // Página dedicada de gerenciamento da reserva (13-08-2026) — substitui o
    // `GerenciarDialog` (modal) como destino do botão "Gerenciar" da listagem.
    // `\d+` (nunca `nova`) evita colisão com a rota acima — `/app/reservas/nova`
    // é avaliada antes E o padrão numérico já a exclui por construção.
    path: /^\/app\/reservas\/(\d+)\/?$/,
    key: "user-reserva-detalhe",
    breadcrumb: [
      { label: "Dashboard", href: "/app/dashboard" },
      { label: "Minhas Reservas", href: "/app/reservas" },
      { label: "Reserva" },
    ],
    chrome: "react" as const,
    render: guarded(codename("hotelaria.user.reservas", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "user-reserva-detalhe",
        breadcrumb: [
          { label: "Dashboard", href: "/app/dashboard" },
          { label: "Minhas Reservas", href: "/app/reservas" },
          { label: "Reserva" },
        ],
        zone: "user" as const,
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/reserva-detalhe").then((m) => m.renderReservaDetalhePage(root, shell));
    }),
  },
  // 04-09-2026 (encerramento da ADR-029) — "Intercâmbio" virou "Em breve":
  // decisão do dono de não manter dado mockado em produção. path/key/
  // breadcrumb/permissão preservados 1:1.
  defineComingSoonRoute(
    /^\/app\/intercambio\/?$/,
    "user-intercambio",
    "Intercâmbio",
    [{ label: "Dashboard", href: "/app/dashboard" }, { label: "Intercâmbio" }],
    { zone: "user", permission: codename("hotelaria.user.intercambio", "view") },
  ),
  {
    path: /^\/app\/extrato\/?$/,
    key: "user-extrato",
    breadcrumb: [{ label: "Dashboard", href: "/app/dashboard" }, { label: "Extrato Financeiro" }],
    // Sunset ADR-009, Estágio 2, Lote 3 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/user/extrato.ts) removido. guarded(codename) preservado 1:1.
    chrome: "react" as const,
    render: guarded(codename("cotista.user.financeiro", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "user-extrato",
        breadcrumb: [{ label: "Dashboard", href: "/app/dashboard" }, { label: "Extrato Financeiro" }],
        zone: "user" as const,
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/extrato").then((m) => m.renderExtratoPage(root, shell));
    }),
  },
  {
    path: /^\/app\/boletos\/?$/,
    key: "user-boletos",
    breadcrumb: [{ label: "Dashboard", href: "/app/dashboard" }, { label: "Boletos/Faturas" }],
    // Onda 5 (ADR-011 — ver docs/migracao-react/ONDA-5-preparacao-user-boletos.md).
    // Fallback Vanilla (pages/user/boletos.ts) removido. guarded(codename) preservado 1:1.
    chrome: "react" as const,
    render: guarded(codename("cotista.user.boletos", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "user-boletos",
        breadcrumb: [{ label: "Dashboard", href: "/app/dashboard" }, { label: "Boletos/Faturas" }],
        zone: "user" as const,
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/boletos").then((m) => m.renderBoletosPage(root, shell));
    }),
  },
  {
    path: /^\/app\/taxa-condominio\/?$/,
    key: "user-taxa-condominio",
    breadcrumb: [{ label: "Dashboard", href: "/app/dashboard" }, { label: "Taxa de Condomínio" }],
    // View complementar de "Boletos" (mesmo componente, `modo="condominio"` —
    // ver decisão de produto no topo de react/pages/boletos.tsx). Mesma
    // permissão de boletos: não existe permissão granular própria.
    chrome: "react" as const,
    render: guarded(codename("cotista.user.boletos", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "user-taxa-condominio",
        breadcrumb: [{ label: "Dashboard", href: "/app/dashboard" }, { label: "Taxa de Condomínio" }],
        zone: "user" as const,
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/boletos").then((m) => m.renderTaxaCondominioPage(root, shell));
    }),
  },
  {
    path: /^\/app\/documentos\/?$/,
    key: "user-documentos",
    breadcrumb: [{ label: "Dashboard", href: "/app/dashboard" }, { label: "Documentos" }],
    chrome: "react" as const,
    render: guarded(codename("cotista.user.documentos", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "user-documentos",
        breadcrumb: [{ label: "Dashboard", href: "/app/dashboard" }, { label: "Documentos" }],
        zone: "user" as const,
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/documentos").then((m) => m.renderDocumentosPage(root, shell));
    }),
  },
  {
    path: /^\/app\/perfil\/?$/,
    key: "user-perfil",
    breadcrumb: [{ label: "Dashboard", href: "/app/dashboard" }, { label: "Meu Perfil" }],
    // Sunset ADR-009, Estágio 2, Lote 3 — ver
    // docs/migracao-react/SUNSET-PLANO-TECNICO-EXECUCAO.md. Fallback Vanilla
    // (pages/user/perfil.ts) removido. guarded(codename) preservado 1:1.
    chrome: "react" as const,
    render: guarded(codename("cotista.user.perfil", "view"), (root: HTMLElement) => {
      const shell = {
        activeRoute: "user-perfil",
        breadcrumb: [{ label: "Dashboard", href: "/app/dashboard" }, { label: "Meu Perfil" }],
        zone: "user" as const,
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/perfil").then((m) => m.renderPerfilPage(root, shell));
    }),
  },
  {
    path: /^\/app\/comunicados\/?$/,
    key: "user-comunicados",
    breadcrumb: [{ label: "Dashboard", href: "/app/dashboard" }, { label: "Comunicados" }],
    // Migração React MUI (ADR-011) — fallback Vanilla (pages/user/comunicados.ts) removido.
    // Visualização individual por comunicado (Single Page, nunca modal) é a
    // MESMA rota — id vem de `?id=<id>` (query param), não de path param, pra
    // não tratar o comunicado como um sub-recurso de URL (`/comunicados/67`
    // sugere um path REST; o id aqui é só um parâmetro de qual comunicado
    // exibir na tela "Comunicados"). `router.ts` casa só por pathname, então
    // as 2 telas (lista/detalhe) cabem numa rota só, decidindo por query aqui.
    chrome: "react" as const,
    render: guarded(codename("cotista.user.comunicados", "view"), (root: HTMLElement) => {
      const temId = new URLSearchParams(window.location.search).has("id");
      const shell = {
        activeRoute: "user-comunicados",
        breadcrumb: temId
          ? [
            { label: "Dashboard", href: "/app/dashboard" },
            { label: "Comunicados", href: "/app/comunicados" },
            { label: "Comunicado" },
          ]
          : [{ label: "Dashboard", href: "/app/dashboard" }, { label: "Comunicados" }],
        permissions: myPermissions,
        isSuperadmin: me.is_superadmin,
        userRole: me.role,
      };
      return import("./react/pages/comunicados").then((m) =>
        temId ? m.renderComunicadoDetalhePage(root, shell) : m.renderComunicadosPage(root, shell),
      );
    }),
  },
  {
    path: "/__router-preview",
    key: "router-preview",
    breadcrumb: [{ label: "Router Preview (Spike B)" }],
    // Spike B (ADR-012) — rota de teste isolada, nunca linkada pelo app real
    // (sem item de nav, sem permissão associada). Valida <Router location
    // navigator> montado como filho de AppProviders/DashboardLayout,
    // reaproveitando mountReact()/root-registry.ts (ADR-013) sem mecanismo
    // novo. Ver ROUTER-PLANO-TECNICO.md §"Design do Spike B".
    chrome: "react" as const,
    render: (root: HTMLElement) =>
      import("./react/router-bridge").then((m) =>
        m.renderReactRouterLocation(root, sharedHistory, {
          zone: "user",
          activeRoute: "router-preview",
          breadcrumb: [{ label: "Router Preview (Spike B)" }],
          permissions: myPermissions,
          isSuperadmin: me.is_superadmin,
          userRole: me.role,
        }),
      ),
  },
  // Redirect legado: /app/comunicados/<id> (path param, formato anterior) →
  // /app/comunicados?id=<id> (query param) — mesmo padrão de
  // defineInternalRedirect acima. Preserva `from` e demais query params
  // já presentes.
  defineInternalRedirect(
    /^\/app\/comunicados\/\d+\/?$/,
    "user-comunicados",
    (path, search) => {
      const match = path.match(/\/app\/comunicados\/(\d+)/);
      const id = match ? match[1] : "";
      const params = new URLSearchParams(search);
      params.set("id", id);
      return `/app/comunicados?${params.toString()}`;
    },
  ),
];

/**
 * Calcula o destino de um "hard redirect" (reload completo da aba) a partir
 * do path atual — hoje cobre o dispatch pós-login de `/painel/` e o guard de
 * zona (`/painel/*` para usuário sem permissão de admin/superadmin). Só
 * CALCULA o destino, nunca executa o `window.location.replace()` ela mesma —
 * quem executa é `handle()` (router.ts), via parâmetro `resolveHardRedirect`
 * de `startRouter()`.
 */
function resolveHardRedirect(path: string): string | null {
  // /painel/ is the post-login dispatch path (Django proxied by Vite → SPA handles instead).
  if (path === "/painel/" || path === "/painel") {
    return homePathForRole(me);
  }

  // Guard: /painel/* — admin, operador ou superadmin only (ADR-038: Operador
  // tem `role` próprio, não mais `role="admin"` combinado com tier). Mesma
  // regra de homePathForRole (não duplicar) — para quem cai aqui (nenhum dos
  // três), ela sempre resolve para "/app/dashboard".
  if (path.startsWith("/painel/") && me.role !== "admin" && me.role !== "operator" && !me.is_superadmin) {
    return homePathForRole(me);
  }

  return null;
}

/**
 * Sunset ADR-009, Estágio 4 — concluído em 2026-08-14.
 *
 * Toda rota declara `chrome: "react"` (agora obrigatório no tipo `Route`) e o
 * handler de 404 também, então a chrome é **sempre** desenhada pelo
 * `DashboardLayout` React. O shell vanilla (`shell.ts`), o kill-switch
 * `isReactChromeEnabled()` e o fallback que os invocava foram removidos.
 *
 * Não desmonta nem limpa o container (ADR-013): `react/bridge.ts` reaproveita o
 * root React entre rotas, reconciliando em vez de remontar
 * `AppProviders`/`DashboardLayout`.
 */
function mountChrome(): HTMLElement {
  return appRoot;
}

/**
 * Sunset ADR-009, Estágio 4 — handler de 404 passado a `startRouter`. Segue o
 * mesmo padrão `import()` dinâmico das demais rotas React; `zone` é calculada
 * (não fixa) porque o 404 é alcançável tanto em `/app/*` quanto em
 * `/painel/*`. `router.ts::handle()` precisou passar `chrome: "react"` na
 * chamada de `mountChrome("404", …)` — senão este handler nunca chegaria a
 * rodar dentro da chrome React.
 */
/**
 * ClickUp #868kt003c — handler do caso degenerado de `guarded()` (ver
 * comentário ali): substitui a área de conteúdo por um placeholder quando a
 * rota negada é a própria home do papel do usuário. Mesmo padrão de
 * `renderNotFoundReact` abaixo (zona calculada pelo path, import dinâmico).
 */
function renderAccessDeniedReact(root: HTMLElement): Promise<void> {
  const path = window.location.pathname;
  const zone = path.startsWith("/app/") ? "user" : "superadmin";
  const shell = {
    zone: zone as "user" | "superadmin",
    activeRoute: "access-denied",
    breadcrumb: [],
    permissions: myPermissions,
    isSuperadmin: me.is_superadmin,
    userRole: me.role,
  };
  return import("./react/pages/access-denied").then((m) => m.renderAccessDeniedPage(root, shell));
}

function renderNotFoundReact(root: HTMLElement): Promise<void> {
  const path = window.location.pathname;
  const zone = path.startsWith("/app/") ? "user" : "superadmin";
  const shell = {
    zone: zone as "user" | "superadmin",
    activeRoute: "404",
    breadcrumb: [{ label: "Página não encontrada" }],
    permissions: myPermissions,
    isSuperadmin: me.is_superadmin,
    userRole: me.role,
  };
  return import("./react/pages/not-found").then((m) => m.renderNotFoundPage(root, shell));
}

startRouter(
  appRoot,
  routes,
  renderNotFoundReact,
  mountChrome,
  () => {
    hideSplash();
    // Só depois do splash liberado (1ª rota já carregou seus dados críticos,
    // ou o timeout de segurança venceu) — ver comentário de `prefetchReservasPorCota`.
    prefetchReservasPorCota();
  },
  resolveHardRedirect,
);